/**
 * useLocationTransition Hook
 *
 * Manages transition animations between campus locations.
 * Respects reduced motion preferences and provides transition configuration.
 */

import { useCallback, useMemo, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSpatialStore } from '../stores/spatialStore';
import { CampusLocation, TransitionType } from '../types/spatial';
import { CAMPUS_LAYOUT } from '../config/locationConfig';
import { NAVIGATION_CONSTANTS } from '../config/gameConstants';

export interface TransitionConfig {
  type: TransitionType;
  direction?: 'left' | 'right' | 'up' | 'down';
  duration: number;
}

export interface UseLocationTransitionResult {
  /** Whether a transition is currently in progress */
  isTransitioning: boolean;
  /** Set the transitioning state */
  setTransitioning: (transitioning: boolean) => void;
  /** Get transition configuration based on from/to locations */
  getTransitionConfig: (
    fromLocation: CampusLocation,
    toLocation: CampusLocation
  ) => TransitionConfig;
  /** Whether the user prefers reduced motion */
  prefersReducedMotion: boolean;
  /** Current transition preferences from store */
  transitionPreferences: {
    enabled: boolean;
    reducedMotion: boolean;
    defaultType: TransitionType;
    defaultDuration: number;
  };
}

/**
 * Calculate the direction of transition based on campus positions
 */
function calculateDirection(
  fromLocation: CampusLocation,
  toLocation: CampusLocation
): 'left' | 'right' | 'up' | 'down' {
  const fromPos = CAMPUS_LAYOUT[fromLocation];
  const toPos = CAMPUS_LAYOUT[toLocation];

  const dx = toPos.x - fromPos.x;
  const dz = toPos.z - fromPos.z;

  // Use the axis with the larger difference
  if (Math.abs(dx) > Math.abs(dz)) {
    return dx > 0 ? 'right' : 'left';
  }
  return dz > 0 ? 'down' : 'up';
}

/**
 * Hook for managing location transitions
 */
export function useLocationTransition(): UseLocationTransitionResult {
  const { isTransitioning, setTransitioning, transitionPreferences } =
    useSpatialStore(
      useShallow((state) => ({
        isTransitioning: state.isTransitioning,
        setTransitioning: state.setTransitioning,
        transitionPreferences: state.transitionPreferences,
      }))
    );

  // Check system preference for reduced motion
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  /**
   * Get the appropriate transition configuration based on locations and preferences
   */
  const getTransitionConfig = useCallback(
    (
      fromLocation: CampusLocation,
      toLocation: CampusLocation
    ): TransitionConfig => {
      // Respect reduced motion preferences
      if (
        prefersReducedMotion ||
        transitionPreferences.reducedMotion ||
        !transitionPreferences.enabled
      ) {
        return {
          type: 'none',
          duration: 0,
        };
      }

      const direction = calculateDirection(fromLocation, toLocation);
      const transitionType = transitionPreferences.defaultType;

      // Use slide for horizontal movements, fade for vertical or special cases
      let type: TransitionType = transitionType;
      if (
        fromLocation === CampusLocation.CAMPUS_OVERVIEW ||
        toLocation === CampusLocation.CAMPUS_OVERVIEW
      ) {
        // Use zoom when entering/leaving campus overview
        type = 'zoom';
      } else if (transitionType === 'slide') {
        type = 'slide';
      }

      return {
        type,
        direction,
        duration:
          transitionPreferences.defaultDuration ||
          NAVIGATION_CONSTANTS.transition.defaultDuration,
      };
    },
    [prefersReducedMotion, transitionPreferences]
  );

  return useMemo(
    () => ({
      isTransitioning,
      setTransitioning,
      getTransitionConfig,
      prefersReducedMotion,
      transitionPreferences,
    }),
    [
      isTransitioning,
      setTransitioning,
      getTransitionConfig,
      prefersReducedMotion,
      transitionPreferences,
    ]
  );
}

export default useLocationTransition;
